import React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import WorkspaceRoutes from './WorkspaceRoutes';
import ProtectedRoute from '@/components/ProtectedRoute';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { WorkspaceErrorBoundary } from '@/components/WorkspaceErrorBoundary';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from '@/components/ui/sonner';

// Import pages (many are lazy-loaded in App.tsx; import the commonly-used ones)
import ModernAuth from '@/pages/ModernAuth';
import ModernOTPVerification from '@/pages/ModernOTPVerification';
import ChangePassword from '@/pages/ChangePassword';
import Auth from '@/pages/Auth';
import OTPVerification from '@/pages/OTPVerification';
import Dashboard from '@/pages/Dashboard';
import Partners from '@/pages/Partners';
import ManageSubscription from '@/pages/ManageSubscription';
import CollectorSandbox from '@/pages/BillCollection/CollectorSandbox';
import ParserSandbox from '@/pages/BillParser/ParserSandbox';
import { SelectWorkspace } from '@/pages/SelectWorkspace';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import NotFound from '@/pages/NotFound';
import ForgotPasswordPage from '@/pages/ForgotPassword';
const AccountRoutes = React.lazy(() => import('./AccountRoutes'));
const InboxRoutes = React.lazy(() => import('./InboxRoutes'));
import { InviteAcceptWrapper as InviteAcceptWrapperNamed } from '@/components/InviteAcceptWrapper';
import { SelectionProvider } from '@/selection/SelectionProvider';
import { loadWorkspaceId } from '@/lib/workspaceStorage';

// Lazy pages and workspace nested pages are still referenced via their original imports in the main app.

// DefaultWorkspaceRedirect removed; /dashboard is now handled within WorkspaceRoutes using session-resolved workspaceId.

function WorkspaceRoute() {
  // Gate: if no workspace is selected in session, redirect to the selector before mounting providers
  const [ready, setReady] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  React.useEffect(() => {
    try {
      // Attempt to hydrate from localStorage backup before redirecting
      const id = loadWorkspaceId();
      if (!id && !location.pathname.startsWith('/select-workspace')) {
        navigate('/select-workspace', { replace: true });
        return;
      }
    } catch (_) {}
    setReady(true);
  }, [navigate, location.pathname]);

  if (!ready) {
    return <div className="p-8 text-center text-muted-foreground">Preparing workspace…</div>;
  }

  return (
    <WorkspaceErrorBoundary>
      <WorkspaceProvider>
        <DashboardLayout />
      </WorkspaceProvider>
    </WorkspaceErrorBoundary>
  );
}

export default function AppRoutes() {
  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<ModernAuth />} />
        <Route path="/select-workspace" element={
          <SelectionProvider>
            <SelectWorkspace />
          </SelectionProvider>
        } />
        <Route path="/auth" element={<ModernAuth />} />
        <Route path="/auth-old" element={<Auth />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/verify-otp" element={<ModernOTPVerification />} />
        <Route path="/verify-otp-old" element={<OTPVerification />} />
  <Route path="/forgot-password" element={<ForgotPasswordPage />} />

  {/* Dashboard mounted with workspace providers and context; workspaceId resolved from session */}
  <Route path="/dashboard" element={<WorkspaceRoute />}>
    <Route index element={<Dashboard />} />
    <Route path="partners" element={<Partners />} />
    <Route path="manage-subscription" element={<ManageSubscription />} />
    <Route path="collector-sandbox" element={<CollectorSandbox />} />
    <Route path="parser-sandbox" element={<ParserSandbox />} />
  </Route>

        {/* Privacy & Terms routes */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />

        {/* Global Account Routes (rendered inside DashboardLayout/Workspace providers) */}
        <Route element={<WorkspaceRoute />}>
          <Route path="account/*" element={
            <React.Suspense fallback={<div className="p-4 text-center">Loading account...</div>}>
              <AccountRoutes />
            </React.Suspense>
          } />
        </Route>

        {/* Inbox Routes (rendered inside DashboardLayout/Workspace providers) */}
        <Route element={<WorkspaceRoute />}>
          <Route path="inbox/*" element={
            <React.Suspense fallback={<div className="p-4 text-center">Loading inbox...</div>}>
              <InboxRoutes />
            </React.Suspense>
          } />
        </Route>

        {/* Invitation acceptance routes */}
        <Route path="/invite/accept/:token" element={<InviteAcceptWrapperNamed routeType="path-param" />} />
        <Route path="/invite/accept" element={<InviteAcceptWrapperNamed routeType="query-param" />} />

        {/* Workspace-scoped routes (group mounted under /w/*) */}
        <Route path="/w/*" element={
          <React.Suspense fallback={<div className="p-4 text-center">Loading workspace...</div>}>
            <WorkspaceRoutes />
          </React.Suspense>
        } />

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster />
      {/* Sonner toast system (used across many pages) - positioned bottom-right per user request */}
      <SonnerToaster position="bottom-right" richColors closeButton />
    </>
  );
}
